import { useState, useEffect } from 'react'

interface GeolocationState {
    location: { lat: number; lng: number } | null
    loading: boolean
    error: string | null
    permission: PermissionState | null
}

interface UseGeolocationOptions {
    enableHighAccuracy?: boolean
    timeout?: number
    maximumAge?: number
}

export function useGeolocation(options: UseGeolocationOptions = {}) {
    const [state, setState] = useState<GeolocationState>({
        location: null,
        loading: false,
        error: null,
        permission: null,
    })

    const {
        enableHighAccuracy = true,
        timeout = 10000,
        maximumAge = 300000, // 5 minutes
    } = options

    const requestLocation = () => {
        if (!navigator.geolocation) {
            setState(prev => ({
                ...prev,
                error: 'Geolocation is not supported by this browser',
                loading: false,
            }))
            return
        }

        setState(prev => ({ ...prev, loading: true, error: null }))

        navigator.geolocation.getCurrentPosition(
            (position) => {
                console.log('📍 Location obtained:', position.coords.latitude, position.coords.longitude)
                setState(prev => ({
                    ...prev,
                    location: {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    },
                    loading: false,
                    error: null,
                    permission: 'granted',
                }))
            },
            (error) => {
                console.error('📍 Geolocation error:', error)
                let errorMessage = 'Failed to get your location'
                let permissionState = state.permission
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = 'Location access denied by user'
                        permissionState = 'denied'
                        break
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = 'Location information unavailable'
                        break
                    case error.TIMEOUT:
                        errorMessage = 'Location request timed out'
                        break
                }

                setState(prev => ({
                    ...prev,
                    error: errorMessage,
                    loading: false,
                    permission: permissionState,
                }))
            },
            {
                enableHighAccuracy,
                timeout,
                maximumAge,
            }
        )
    }

    // Check permission status and listen for changes
    useEffect(() => {
        let permissionResult: PermissionStatus | null = null
        let handlePermissionChange: (() => void) | null = null

        // Check if Permissions API is available (not supported in Safari)
        if ('permissions' in navigator && 'query' in navigator.permissions) {
            navigator.permissions.query({ name: 'geolocation' }).then((result) => {
                permissionResult = result
                setState(prev => ({ ...prev, permission: result.state }))

                // Listen for permission changes
                handlePermissionChange = () => {
                    console.log('📍 Permission changed to:', result.state)
                    setState(prev => ({ ...prev, permission: result.state }))

                    // Auto-request if permission is granted
                    if (result.state === 'granted') {
                        requestLocation()
                    }
                }

                result.addEventListener('change', handlePermissionChange)

                // Auto-request if permission is already granted
                if (result.state === 'granted') {
                    requestLocation()
                }
            }).catch((error) => {
                console.log('📍 Permissions API not fully supported:', error)
                // For Safari and other browsers without full Permissions API support
                setState(prev => ({ ...prev, permission: 'prompt' }))
            })
        } else {
            // Fallback for browsers without Permissions API (like Safari)
            console.log('📍 Permissions API not available, assuming prompt state')
            setState(prev => ({ ...prev, permission: 'prompt' }))
        }

        // Cleanup function
        return () => {
            if (permissionResult && handlePermissionChange) {
                permissionResult.removeEventListener('change', handlePermissionChange)
            }
        }
    }, [])

    return {
        ...state,
        requestLocation,
    }
} 